'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { attachLevelMeter } from '../audio/levelMeter';

/**
 * Return type for useAudioRecorder hook
 */
export interface UseAudioRecorderReturn {
  // State
  isRecording: boolean;
  isPaused: boolean;
  duration: number; // seconds elapsed
  level: number; // live mic level 0..1 while recording (0 when idle)
  audioBlob: Blob | null; // recorded audio blob
  audioUrl: string | null; // object URL for preview playback
  error: string | null;
  mimeType: string; // actual MIME type used (webm or mp4)

  // Actions
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  resetRecording: () => void;
}

/**
 * Options for useAudioRecorder hook
 */
export interface UseAudioRecorderOptions {
  maxDurationSeconds?: number; // default: 300 (5 min)
}

/**
 * React hook that wraps the browser MediaRecorder API for voice recording.
 *
 * Features:
 * - Auto-detects best supported MIME type (webm with opus codec preferred, mp4 fallback)
 * - Tracks recording duration with automatic stop at max duration
 * - Provides pause/resume functionality
 * - Generates object URL for playback preview
 * - Handles permission errors gracefully
 * - Cleans up resources on unmount
 *
 * @param options - Configuration options
 * @returns Recording state and control methods
 */
export function useAudioRecorder(
  options?: UseAudioRecorderOptions
): UseAudioRecorderReturn {
  const maxDurationSeconds = options?.maxDurationSeconds ?? 300;

  // State
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // Live input level (0..1, RMS of the mic signal) so the UI can show that the
  // microphone actually hears something — a ticking timer alone says nothing.
  const [level, setLevel] = useState(0);
  const stopLevelMeterRef = useRef<(() => void) | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const lastPauseTimeRef = useRef<number>(0);

  /**
   * Determine the best supported MIME type for audio recording
   */
  const getSupportedMimeType = useCallback((): string => {
    const types = ['audio/webm;codecs=opus', 'audio/mp4'];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // Fallback to default
    return 'audio/webm';
  }, []);

  /**
   * Cleanup media stream tracks
   */
  const cleanupMediaStream = useCallback(() => {
    stopLevelMeterRef.current?.();
    stopLevelMeterRef.current = null;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  /**
   * Cleanup interval timer
   */
  const cleanupInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Update duration timer
   */
  const updateDuration = useCallback(() => {
    if (!isRecording || isPaused) return;

    const elapsed = Math.floor((Date.now() - startTimeRef.current - pausedDurationRef.current) / 1000);
    setDuration(elapsed);

    // Auto-stop at max duration
    if (elapsed >= maxDurationSeconds) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }
  }, [isRecording, isPaused, maxDurationSeconds]);

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    // Prevent starting if already recording
    if (isRecording) {
      return;
    }

    try {
      setError(null);

      // Check that mediaDevices API is available (requires secure context: HTTPS or localhost)
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('microphone_not_available');
        return;
      }

      // Request microphone access (triggers browser permission dialog if needed)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      stopLevelMeterRef.current = attachLevelMeter(stream, setLevel);

      // Determine MIME type
      const supportedMimeType = getSupportedMimeType();
      setMimeType(supportedMimeType);

      // Create MediaRecorder
      const recorder = new MediaRecorder(stream, {
        mimeType: supportedMimeType,
      });

      mediaRecorderRef.current = recorder;

      // Handle data available
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          const blob = event.data;
          setAudioBlob(blob);

          // Create object URL for playback
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
        }
      };

      // Handle recording stop
      recorder.onstop = () => {
        setIsRecording(false);
        setIsPaused(false);
        cleanupInterval();
        cleanupMediaStream();
      };

      // Handle errors
      recorder.onerror = (event: Event) => {
        setError(`Recording error: ${(event as ErrorEvent).message}`);
        setIsRecording(false);
        setIsPaused(false);
        cleanupInterval();
        cleanupMediaStream();
      };

      // Start recording
      recorder.start();
      setIsRecording(true);
      setIsPaused(false);

      // Initialize timing
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;
      setDuration(0);
    } catch (err) {
      // Map specific error types to sentinel strings
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('microphone_permission_denied');
          setIsRecording(false);
          cleanupMediaStream();
          return;
        }
        if (err.name === 'NotFoundError') {
          setError('microphone_not_found');
          setIsRecording(false);
          cleanupMediaStream();
          return;
        }
      }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setIsRecording(false);
      cleanupMediaStream();
    }
  }, [isRecording, getSupportedMimeType, cleanupInterval, cleanupMediaStream]);

  /**
   * Stop recording audio
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  /**
   * Pause recording audio
   */
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      lastPauseTimeRef.current = Date.now();
    }
  }, []);

  /**
   * Resume recording audio
   */
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      // Track paused duration
      const pauseDuration = Date.now() - lastPauseTimeRef.current;
      pausedDurationRef.current += pauseDuration;
    }
  }, []);

  /**
   * Reset recording state
   */
  const resetRecording = useCallback(() => {
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // Cleanup resources
    cleanupInterval();
    cleanupMediaStream();

    // Revoke object URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    // Reset state
    setIsRecording(false);
    setIsPaused(false);
    setDuration(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setError(null);
    startTimeRef.current = 0;
    pausedDurationRef.current = 0;
    lastPauseTimeRef.current = 0;
  }, [cleanupInterval, cleanupMediaStream, audioUrl]);

  // Effect: Update duration while recording
  useEffect(() => {
    if (isRecording && !isPaused) {
      intervalRef.current = setInterval(updateDuration, 1000);

      return () => {
        cleanupInterval();
      };
    } else {
      cleanupInterval();
    }
  }, [isRecording, isPaused, updateDuration, cleanupInterval]);

  // Effect: Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      stopLevelMeterRef.current?.();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return {
    // State
    isRecording,
    isPaused,
    duration,
    /** Live mic level 0..1 while recording (0 when idle). */
    level,
    audioBlob,
    audioUrl,
    error,
    mimeType,

    // Actions
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
  };
}
